import cx from 'classnames';
import { BigButton } from '../../common';
import { useUserContext } from '../../contexts/UserContext';
import styles from './FloatingCardUI.module.scss';
import { navigateToPlans, navigateToUpgradePlan } from './subscriptionHelper';
import { apiGetPendingPackRequests } from '../../api/subscription/subscription';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { REQUESTS_ROUTE } from '../../config/constant/routePathConstant';
import { selectUserPlanList } from '../../redux/slicers/subscriptionSlicer';
import { useSelector } from 'react-redux';
import { getDateLeftFromDate, navigateToNewRequest, openFormInCaseOfNewOrgAndFreemiumUser, SortArrayBySeq } from '../../helpers/jsHelper';
import { TEMPLATES_ROUTE_V2 } from '../../constants/routePathConstants';
import { StrikeIcon } from '../../assets/icons';
import Button from '../../components/Button';

const RangeBarUI = ({ value = 0, min = 0, max = 1 }) => {
  if (value === null || value === undefined) return null;
  if (min > max) {
    console.log('Minimum value should be less than Maximum value');
    return null;
  }
  if (value < min || value > max) {
    console.log('Value should be between Minimum and Maximum value');
    return null;
  }
  return (
    <div className="d-flex flex-column gap-1">
      <div className={styles.range}>
        <div
          className={styles.progress}
          style={{ width: `${((value - min) / (max - min)) * 100}%` }}></div>
      </div>
      <div className={cx('d-flex align-items-center justify-content-between', styles.description)}>
        <span>Time</span>
        <span>
          Day {value} of {max}
        </span>
      </div>
    </div>
  );
};

export const FloatingCardUI = ({
  title = '',
  subTitle = '',
  description = '',
  buttonText = '',
  buttonIcon = null,
  disabledButton = false,
  showBtn = true,
  onClick = () => {},
  btnClassName = '',
  rangeProps = null,
  keepButtonOnly = false,
  containerClassName
}) => {
  if (keepButtonOnly && showBtn && buttonText) {
    return (
      <div className={cx(styles.container, styles.noBackground, containerClassName || "")}>
        <Button disabled={disabledButton} className={btnClassName} size="lg" onClick={onClick}>
          {buttonIcon}&nbsp;{buttonText}
        </Button>
      </div>
    );
  }

  if (!title) return null;

  return (
    <div className={styles.container}>
      {subTitle ? (
        <>
          <div className={styles.innerContainer}>
            <h5 className={styles.title}>{title}</h5>
            <p className={styles.description}>{subTitle}</p>
          </div>
          <hr className={styles.line} />
          <div className={styles.innerContainer}>
            <p className={styles.description}>{description}</p>
          </div>
        </>
      ) : (
        <>
          <div className={styles.innerContainer}>
            <h5 className={styles.title}>{title}</h5>
            <p className={styles.description}>{description}</p>
          </div>
        </>
      )}

      {rangeProps ? <RangeBarUI {...rangeProps} /> : null}

      {showBtn && buttonText ? (
        <BigButton
          className={cx('bigButton w-100 lf-btn-1 btn-v2', btnClassName)}
          buttonTitle={buttonText}
          onClick={onClick}
          disabled={disabledButton}
          iconBefore={buttonIcon}
        />
      ) : null}
    </div>
  );
};

export const YouAreOnFreemium = ({containerClassName}) => {
  const { isFreemiumUser, isGuestUser, isPackOrganization } = useUserContext();
  const showFreemiumCard = isFreemiumUser();
  if (!showFreemiumCard || isPackOrganization) return null;

  const handleShowEnterPriceForm = () =>{
    openFormInCaseOfNewOrgAndFreemiumUser();
    return;
  }

  return (
    <FloatingCardUI
      // title="You’re on Free plan"
      // description="Upgrade to unlock all of our features"
      buttonText="Upgrade"
      // onClick={navigateToUpgradePlan}
      // Temporary purpose we hide all plan so we added the enterprise form here 
      onClick={handleShowEnterPriceForm}
      showBtn={!isGuestUser()}
      keepButtonOnly={true}
      buttonIcon={<StrikeIcon height={18} />}
      containerClassName={containerClassName}
    />
  );
};

export const YouAreOnPackSubscription = () => {
  const navigate = useNavigate();
  const {
    isGuestUser,
    currentUser,
    updateUserData,
    isPackOrganization,
    isFreemiumUser,
    isPremiumUser,
  } = useUserContext();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [leftDays, setLeftDays] = useState(0);

  const getUserPlans = useSelector(selectUserPlanList);
  const isOldPremiumUser = isPremiumUser() && getUserPlans?.length === 0;

  const checkIfHaveAnyPendingPackRequests = async () => {
    const results = await apiGetPendingPackRequests();
    const sortedData = SortArrayBySeq(results?.results, 'product_link_number');
    // keeping the pack with 3d model request first
    setData(
      [
        ...sortedData.filter((a) => !a.is_existing_model),
        ...sortedData.filter((a) => a.is_existing_model),
      ]?.[0]
    );
    setLeftDays(getDateLeftFromDate({ date: results?.pack_expiry_date }));
  };

  const getData = async () => {
    setLoading(true);
    await checkIfHaveAnyPendingPackRequests();
    updateUserData();
    setLoading(false);
  };

  useEffect(() => {
    if (isGuestUser()) return;
    getData();
  }, [currentUser?.organization, currentUser?.user?.id]);

  const buttonText = isOldPremiumUser ? 'See your packs' : 'Explore';
  const buttonClick = isOldPremiumUser
    ? () => navigateToPlans()
    : () => navigate(TEMPLATES_ROUTE_V2); // navigateToUpgradePlan();

  const raiseRequests = useCallback(
    () =>
      data
        ? navigateToNewRequest({
            purchase_id: data?.id,
            seq: data?.product_link_number,
            is_existing_model: data?.is_existing_model,
          })
        : navigate(REQUESTS_ROUTE),
    [data]
  );

  if (loading) return null;

  return (
    <>
      {data && leftDays > 0 ? (
        <FloatingCardUI
          title="Time is running!"
          description="Request your deliverables for download before your subscription ends."
          buttonText="Raise request now"
          onClick={raiseRequests}
          disabledButton={loading}
          showBtn={!isGuestUser()}
          rangeProps={{
            value: leftDays,
            min: 0,
            max: 30,
          }}
        />
      ) : isPackOrganization && isFreemiumUser() ? (
        <FloatingCardUI
          title="You’re a Pack User"
          description="Take advantage of all of our features before the end of your subscription."
          buttonText={buttonText}
          onClick={buttonClick}
          showBtn={!isGuestUser()}
          rangeProps={{
            value: leftDays,
            min: 0,
            max: 30,
          }}
        />
      ) : null}
    </>
  );
};
